import {
  PauseIcon,
  PlayIcon,
  SearchIcon,
  SparklesIcon,
  StarIcon as EmptyStarIcon,
  TrashIcon,
} from '@heroicons/react/outline';
import { StarIcon } from '@heroicons/react/solid';
import Image from 'next/image';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Voice } from 'types';

import clsxm from '@/lib/clsxm';

import Spinner from '@/components/Spinner';

import { isMiniMaxVoiceModel } from '@/constants/minimaxVoice';
import axiosInstance from '@/utils/httpClient';
import { useUser } from '@/utils/useUser';
import { getVoiceImageUrl } from '@/utils/voices';

import Select from '../atoms/Select';
import Button from '../buttons/Button';
import { languages as languageFlags } from '../TextToSpeechLanguages';

const flagMap = new Map<string, string>();
for (const lang of languageFlags) {
  // Don't overwrite if already set (first occurrence wins)
  if (!flagMap.has(lang.name)) {
    flagMap.set(lang.name, lang.flag);
  }
}

const getFlag = (name: string): string => flagMap.get(name) ?? '';

// Voices that read audio tags at every speed: the curated expressive voices
// and designed voices (pinned to Eleven v3). Other ElevenLabs voices read
// tags on Eleven v4 only, i.e. at 1.0x speed.
const isExpressiveVoice = (voice: Voice) =>
  Boolean(voice.expressive) || voice.model === 'eleven_v3';

// How many voice cards to render per "page". The list can contain 1.5k+
// voices; mounting them all at once (each with an <Image>) is slow, so we
// render incrementally as the user scrolls.
const PAGE_SIZE = 60;

interface VoiceSelectionProps {
  voices: Voice[];
  setVoice: (voiceName: string) => void;
  setVoiceID: (voiceID: string) => void;
  currentVoice: string | null;
  closeModal: () => void;
  forLandingPage?: boolean;
  initialLanguage?: string;
  initialAccent?: string;
  onLanguageChange?: (language: string) => void;
  onAccentChange?: (accent: string) => void;
  /** Pre-apply the "Audio tags" (v3) filter, e.g. when opened from the tag warning. */
  initialOnlyV3?: boolean;
  /** Show a loading spinner instead of the grid while voices are being fetched. */
  isLoading?: boolean;
}

const VoiceSelection: React.FC<VoiceSelectionProps> = ({
  voices,
  setVoice,
  setVoiceID,
  currentVoice,
  closeModal,
  forLandingPage = false,
  initialLanguage = '',
  initialAccent = '',
  onLanguageChange,
  onAccentChange,
  initialOnlyV3 = false,
  isLoading = false,
}) => {
  const { t } = useLocale('tts');
  const [localVoices, setLocalVoices] = useState<Voice[]>(voices);
  const [selectedLanguage, setSelectedLanguage] =
    useState<string>(initialLanguage);
  const [selectedAccent, setSelectedAccent] = useState<string>(initialAccent);
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPlayingVoice, setCurrentPlayingVoice] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [onlyV3, setOnlyV3] = useState(initialOnlyV3);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(!forLandingPage ? true : false);
  const { isFreePlan } = useUser();
  const [favouriteVoices, setFavouriteVoices] = useState<string[]>([]);

  // Load favorite voices from localStorage
  useEffect(() => {
    const favourites = JSON.parse(
      localStorage.getItem('favouriteVoices') || '[]',
    );
    setFavouriteVoices(favourites);
  }, []);

  // Reflect how the picker was opened: the audio-tag warning opens it with the
  // v3 filter on; a normal "Change voice" opens it off. The modal stays mounted,
  // so sync when the parent flag flips.
  useEffect(() => {
    setOnlyV3(initialOnlyV3);
  }, [initialOnlyV3]);

  // Toggle favorite status for a voice
  const toggleFavourite = (voiceId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const favourites = JSON.parse(
      localStorage.getItem('favouriteVoices') || '[]',
    );
    if (favourites.includes(voiceId)) {
      const updated = favourites.filter((id: string) => id !== voiceId);
      localStorage.setItem('favouriteVoices', JSON.stringify(updated));
      setFavouriteVoices(updated);
    } else {
      const updated = [...favourites, voiceId];
      localStorage.setItem('favouriteVoices', JSON.stringify(updated));
      setFavouriteVoices(updated);
    }
  };

  useEffect(() => {
    // Deduplicate voices by voice_id, keeping the first occurrence.
    // Uses a Set for O(n) lookup instead of O(n²) reduce + find.
    const seen = new Set<string>();
    const deduplicatedVoices: Voice[] = [];
    for (const voice of voices) {
      if (!seen.has(voice.voice_id)) {
        seen.add(voice.voice_id);
        deduplicatedVoices.push(voice);
      }
    }

    setLocalVoices(deduplicatedVoices);

    // Extract all unique languages
    const languages = Array.from(
      new Set(
        deduplicatedVoices
          .flatMap((voice) =>
            Array.isArray(voice.language) ? voice.language : [voice.language],
          )
          .filter(Boolean),
      ),
    );
    setAvailableLanguages(languages as string[]);
  }, [voices]);

  // Derive filtered voices from state (no stale closures possible)
  const filteredVoices = useMemo(() => {
    let filtered = localVoices;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((voice) => {
        const nameMatch = voice.name.toLowerCase().includes(query);
        const labelMatch = voice.labels
          ? Object.values(voice.labels).some((val) =>
              val.toLowerCase().includes(query),
            )
          : false;
        const accentMatch = voice.accent?.toLowerCase().includes(query);
        const langMatch = Array.isArray(voice.language)
          ? voice.language.some((l) => l.toLowerCase().includes(query))
          : voice.language?.toLowerCase().includes(query);
        return nameMatch || labelMatch || accentMatch || langMatch;
      });
    }

    // Apply language filter if selected
    if (selectedLanguage) {
      filtered = filtered.filter((voice) => {
        if (!voice.language) return false;
        return Array.isArray(voice.language)
          ? voice.language.includes(selectedLanguage)
          : voice.language === selectedLanguage;
      });
    }

    // Apply accent filter if selected (independent of language)
    if (selectedAccent) {
      filtered = filtered.filter((voice) => voice.accent === selectedAccent);
    }

    // Show only voices that read audio tags at every speed when toggled.
    if (onlyV3) {
      filtered = filtered.filter(isExpressiveVoice);
    }

    // Sort voices: favorites first
    const favorited = filtered.filter((voice) =>
      favouriteVoices.includes(voice.voice_id),
    );
    const notFavorited = filtered.filter(
      (voice) => !favouriteVoices.includes(voice.voice_id),
    );
    return [...favorited, ...notFavorited];
  }, [
    localVoices,
    selectedLanguage,
    selectedAccent,
    onlyV3,
    favouriteVoices,
    searchQuery,
  ]);

  // Only surface the audio-tag filter when there are tag-capable voices.
  const hasV3Voices = useMemo(
    () => localVoices.some(isExpressiveVoice),
    [localVoices],
  );

  // Derive available accents: when language is selected, only show accents for that language
  const availableAccents = useMemo(() => {
    let voicesForAccents = localVoices;
    if (selectedLanguage) {
      voicesForAccents = localVoices.filter((voice) => {
        if (!voice.language) return false;
        return Array.isArray(voice.language)
          ? voice.language.includes(selectedLanguage)
          : voice.language === selectedLanguage;
      });
    }
    return Array.from(
      new Set(voicesForAccents.map((voice) => voice.accent).filter(Boolean)),
    ) as string[];
  }, [localVoices, selectedLanguage]);

  // When the landing-page list is collapsed, only the first 12 cards are
  // shown. Slice to exactly that count so the hidden cards are never mounted
  // (CSS `hidden` would still mount them and their <Image> elements).
  const collapsedLanding = forLandingPage && !open;

  // Only render the first `visibleCount` voices; more load as the user scrolls.
  const visibleVoices = useMemo(
    () => filteredVoices.slice(0, collapsedLanding ? 12 : visibleCount),
    [filteredVoices, visibleCount, collapsedLanding],
  );

  // Reset pagination whenever the result set changes (search / filters).
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchQuery, selectedLanguage, selectedAccent, onlyV3]);

  // Load the next page when the sentinel scrolls into view.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    if (visibleCount >= filteredVoices.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((count) =>
            Math.min(count + PAGE_SIZE, filteredVoices.length),
          );
        }
      },
      { root: scrollContainerRef.current, rootMargin: '120px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [visibleCount, filteredVoices.length]);

  const playSample = (
    sampleUrl: string,
    voiceName: string,
    voiceId: string,
  ) => {
    if (audioRef.current) {
      audioRef.current.src = sampleUrl;
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
      setCurrentPlayingVoice(voiceName);
      // Auto-select the voice when playing

      if (voiceName === 'Adam' && isFreePlan) {
        return;
      }

      setVoice(voiceName);
      setVoiceID(voiceId);
    }
  };

  const pauseSample = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleAudioEnd = () => {
    setIsPlaying(false);
  };

  const handleDelete = (voice: Voice) => {
    // ask for confirmation
    const confirm = window.confirm(
      t('Are you sure you want to delete this voice?'),
    );
    if (!confirm) {
      return false;
    }

    const newVoices = localVoices.filter((v) => v.voice_id !== voice.voice_id);
    setLocalVoices(newVoices);

    axiosInstance.post('/api/delete-voice', {
      voice_id: voice.voice_id,
    });
  };

  return (
    <div className='mx-auto'>
      <div className='mb-10 mt-10 text-center'>
        <p className='mt-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
          {forLandingPage
            ? t('Explore our variety of voices for different use cases')
            : t('Choose a voice for the audio')}
        </p>
      </div>
      <div className='mb-6 flex flex-col items-center gap-3 px-4 sm:flex-row sm:justify-center sm:gap-4 sm:px-0'>
        <div className='relative w-full sm:w-80'>
          <SearchIcon className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-slate-500' />
          <input
            type='text'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('Search voices...')}
            className='mt-1 block w-full rounded-md border-gray-300 py-2 pl-10 pr-3 text-base placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-primary-500 dark:border-white/5 dark:bg-slate-700/50 dark:text-white dark:placeholder-slate-500 sm:text-sm'
          />
        </div>
        <Select
          options={[
            { value: '', label: t('Language') },
            ...availableLanguages
              .sort((a, b) => t(a).localeCompare(t(b)))
              .map((lang) => ({
                value: lang,
                label: `${getFlag(lang)} ${t(lang)}`.trim(),
              })),
          ]}
          label='Language'
          value={selectedLanguage}
          onChange={(value) => {
            setSelectedLanguage(value);
            onLanguageChange?.(value);
            // Reset the accent filter — accents are language-specific, so a
            // stale accent (e.g. "Singaporean") would filter the newly
            // selected language down to zero voices.
            if (selectedAccent) {
              setSelectedAccent('');
              onAccentChange?.('');
            }
          }}
          className='w-full sm:w-48'
          placeholder={t('Language')}
        />
        <Select
          options={[
            { value: '', label: t('Accent') },
            ...availableAccents
              .sort((a, b) => t(a).localeCompare(t(b)))
              .map((accent) => ({
                value: accent,
                label: `${getFlag(accent)} ${t(accent)}`.trim(),
              })),
          ]}
          label='Accent'
          value={selectedAccent}
          onChange={(value) => {
            setSelectedAccent(value);
            onAccentChange?.(value);
          }}
          className='w-full sm:w-48'
          placeholder={t('Accent')}
        />
        {hasV3Voices && (
          <button
            type='button'
            onClick={() => setOnlyV3((v) => !v)}
            aria-pressed={onlyV3}
            className={clsxm(
              'mt-1 inline-flex w-full items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium transition sm:mt-0 sm:w-auto',
              onlyV3
                ? 'border-primary-500 bg-primary-50 text-primary-600 dark:border-primary-500 dark:bg-primary-900/20 dark:text-primary-300'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-slate-800',
            )}
          >
            <SparklesIcon className='h-4 w-4' />
            {t('Audio tags')}
          </button>
        )}
      </div>
      <div
        ref={scrollContainerRef}
        className='relative grid h-fit w-full max-h-[50vh] sm:max-h-[60vh] grid-cols-1 gap-4 overflow-auto  overflow-x-hidden px-2 sm:p-6 md:grid-cols-3'
      >
        {isLoading && (
          <div className='col-span-1 flex flex-col items-center justify-center gap-3 py-20 text-gray-500 dark:text-slate-400 md:col-span-3'>
            <Spinner className='h-8 w-8 animate-spin text-primary-500' />
            <span className='text-sm'>{t('Loading voices...')}</span>
          </div>
        )}
        {!isLoading &&
          visibleVoices.map((voice) => (
            <div
              key={voice.voice_id}
              className={clsxm(
                'group relative hover:border-primary-500 hover:opacity-100 hover:ring-2 hover:ring-primary-500',
                'hover:border-purple-1 flex cursor-pointer items-center justify-between rounded-xl border border-transparent bg-gray-50 p-4 dark:bg-slate-800',
                currentVoice === voice.name
                  ? 'border-primary-500 bg-gray-100 ring-primary-500'
                  : '',
              )}
              onClick={() => {
                if (voice.name === 'Adam' && isFreePlan) {
                  return;
                }
                setVoice(voice.name);
                setVoiceID(voice.voice_id);
              }}
            >
              <div>
                <p className='mb-2 text-left text-sm font-semibold leading-none text-gray-900 dark:text-slate-200'>
                  {t(voice.name)}
                  {/* {voice.new && (
                  <span className='ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-primary-100 text-primary-800'>
                    {t('New')}
                  </span>
                )} */}
                  {voice.language && (
                    <span className='ml-2 inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium leading-4 text-blue-800'>
                      {getFlag(
                        Array.isArray(voice.language)
                          ? voice.language[0]
                          : voice.language,
                      )}{' '}
                      {Array.isArray(voice.language)
                        ? t(voice.language[0])
                        : t(voice.language)}
                      {voice.accent &&
                        voice.accent.toLowerCase() !==
                          (Array.isArray(voice.language)
                            ? voice.language[0].toLowerCase()
                            : voice.language.toLowerCase()) &&
                        ` (${getFlag(voice.accent)} ${t(voice.accent)})`}
                    </span>
                  )}
                  {voice.isOpenAI && (
                    <span className='ml-2 inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium leading-4 text-green-800'>
                      {t('Saver')}
                    </span>
                  )}
                  {isExpressiveVoice(voice) && (
                    <span className='ml-2 inline-flex items-center gap-0.5 rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium leading-4 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'>
                      <SparklesIcon className='h-3 w-3' />
                      {t('Audio tags')}
                    </span>
                  )}
                </p>
                <div className='flex items-center space-x-2'>
                  <div className=''>
                    <Image
                      alt={t(voice.name)}
                      src={getVoiceImageUrl(voice.image)}
                      width='100'
                      height='100'
                      layout='fixed' // Added layout prop to fix the size of the image
                      className='w-16 rounded-md sm:w-20'
                      loading='lazy'
                    />
                  </div>
                  <div className=' w-1/2'>
                    <p className='text-left text-xs font-medium leading-none text-gray-500 dark:text-slate-400'>
                      {voice.labels
                        ? Object.entries(voice.labels)
                            .filter(
                              ([key]) => key !== 'gender' && key !== 'age',
                            )
                            .map(([_, value]) => t(value))
                            .join(', ')
                        : 'cloned'}
                    </p>
                    {voice.name === 'Adam' && isFreePlan && (
                      <Link
                        href='/settings/billing'
                        className='mt-8  text-xs text-primary-500 hover:underline'
                      >
                        {t('Upgrade')}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
              {voice.preview_url && (
                <div
                  aria-label={
                    isPlaying && currentPlayingVoice === voice.name
                      ? t('Pause')
                      : t('Play')
                  }
                  className='flex h-fit w-fit cursor-pointer items-center justify-center'
                  onClick={(e) => {
                    e.stopPropagation();
                    isPlaying && currentPlayingVoice === voice.name
                      ? pauseSample()
                      : playSample(
                          voice.preview_url as string,
                          voice.name,
                          voice.voice_id,
                        );
                  }}
                >
                  {isPlaying && currentPlayingVoice === voice.name ? (
                    <PauseIcon
                      className='h-10 w-10 text-primary-500'
                      aria-hidden='true'
                    />
                  ) : (
                    <PlayIcon
                      className='h-10 w-10 text-primary-500'
                      aria-hidden='true'
                    />
                  )}
                </div>
              )}
              {/* Professional (MiniMax) voices are not deletable: each costs
                  $1.50 to create and deleting one frees a slot, so delete +
                  re-clone would be an unbounded spend loop. /api/delete-voice
                  enforces this too. */}
              {voice.user_id && !isMiniMaxVoiceModel(voice.model) && (
                <TrashIcon
                  onClick={() => handleDelete(voice)}
                  className='absolute bottom-2 right-2 hidden h-4 w-4 text-gray-500 group-hover:block dark:text-slate-300'
                />
              )}
              {/* Favorite star icon */}
              <div
                className='absolute top-2 right-2'
                onClick={(e) => toggleFavourite(voice.voice_id, e)}
              >
                {favouriteVoices.includes(voice.voice_id) ? (
                  <StarIcon
                    className='h-5 w-5 cursor-pointer text-yellow-400 hover:text-yellow-500'
                    aria-hidden='true'
                  />
                ) : (
                  <EmptyStarIcon
                    className='h-5 w-5 cursor-pointer text-gray-300 hover:text-gray-400 dark:text-slate-500 dark:hover:text-slate-400'
                    aria-hidden='true'
                  />
                )}
              </div>
            </div>
          ))}
        {!isLoading &&
          !collapsedLanding &&
          visibleCount < filteredVoices.length && (
            <div
              ref={sentinelRef}
              className='col-span-1 flex items-center justify-center py-6 md:col-span-3'
              aria-hidden='true'
            >
              <Spinner className='h-6 w-6 animate-spin text-primary-500' />
            </div>
          )}
        {!open && (
          <div className='pointer-events-none absolute -inset-x-2.5 -bottom-2.5 h-[16.6%] bg-gradient-to-t from-white to-white/25 dark:from-slate-900 dark:to-slate-900/25 sm:h-[33.3%]'></div>
        )}
      </div>
      <audio ref={audioRef} onEnded={handleAudioEnd} />
      {!forLandingPage && (
        <div className='flex justify-end bg-gray-50 px-4 py-5 text-right dark:bg-slate-900/50 sm:px-6'>
          <Button variant='outline' onClick={closeModal} className='mr-3'>
            {t('Cancel')}
          </Button>
          <Button variant='dark' onClick={closeModal}>
            {t('Select Voice')}
          </Button>
        </div>
      )}
      {forLandingPage && filteredVoices.length > 15 && (
        <div className='pointer-events-none relative z-10 -mt-5 mb-10 flex justify-center'>
          <Button
            onClick={() => setOpen(!open)}
            className='pointer-events-auto inline-flex justify-center rounded-lg bg-primary-700 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-500'
          >
            <span>{open ? t('Hide') : t('Show more...')}</span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default VoiceSelection;
